import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, Alert, Button, Paper } from '@mui/material';
import { authorizeContext } from '../../hooks/Authorize';
import axiosInstance from '../../utils/axiosInstance';
import { setCookie } from '../../utilities/cookies';

// Reverting to the simpler login URL, assuming accounts.clikkle.com handles the session
// and provides userId/refreshToken on redirect, which Authorize.js is set up to use.
const clikkleLoginBaseUrl = 'https://accounts.clikkle.com/login'; // Or your http://localhost:xxxx equivalent

const AcceptAgentInvitationPage = () => {
  // console.log("AcceptAgentInvitationPage RENDER START");
  const location = useLocation();
  const navigate = useNavigate();
  
  const authData = useContext(authorizeContext);
  const isAuthenticated = !!(authData && authData.user && authData.user._id);
  const currentUser = authData?.user;

  const [status, setStatus] = useState('idle'); 
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [invitationTokenForUI, setInvitationTokenForUI] = useState(null);

  useEffect(() => {
    // Minimal logging to reduce noise, can be re-enabled if needed
    // console.log("Current URL (debug logging):", window.location.href);
  });

  const directlyAcceptInvitation = useCallback(async (token, user) => {
    // console.log('[AcceptAgentInvitationPage] Starting directlyAcceptInvitation', { token, user });
    if (!token || !user) {
      setError("Missing token or user data for direct acceptance.");
      setStatus('error');
      // console.error('[AcceptAgentInvitationPage] Missing token or user data');
      return;
    }
    setStatus('processing_acceptance');
    setMessage('Processing your invitation directly...');
    try {
      const payload = {
        invitationToken: token,
        userDataFromSSO: {
          ssoUserId: user.ssoUserId || user._id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          username: user.username
        }
      };
      // console.log('[AcceptAgentInvitationPage] Sending to /api/agents/accept-invitation:', payload);
      const acceptResponse = await axiosInstance.post('/api/agents/accept-invitation', payload);
      // console.log('[AcceptAgentInvitationPage] Response from /api/agents/accept-invitation:', acceptResponse.data);

      if (acceptResponse.data.success) {
        setMessage(acceptResponse.data.message || 'Invitation accepted successfully!');
        setStatus('success');
        localStorage.removeItem('pendingInvitationToken');
        
        if (acceptResponse.data.organization && acceptResponse.data.orgToken) {
          // console.log('[AcceptAgentInvitationPage] Successfully accepted. Org:', acceptResponse.data.organization, 'Token:', acceptResponse.data.orgToken);
          localStorage.setItem("org", JSON.stringify(acceptResponse.data.organization));
          setCookie("orgToken", acceptResponse.data.orgToken);
          window.location.href = '/listOrganization'; // Force a full reload
        } else {
          // console.error('[AcceptAgentInvitationPage] Org data or token missing in successful response:', acceptResponse.data);
          setError('Organization data or token missing in response.');
          setStatus('error');
        }
      } else {
        // console.error('[AcceptAgentInvitationPage] API reported failure:', acceptResponse.data.error);
        setError(acceptResponse.data.error || 'Failed to accept invitation directly.');
        setStatus('error');
      }
    } catch (err) {
      // console.error('[AcceptAgentInvitationPage] CATCH block error accepting invitation:', err.response ? err.response.data : err.message, err);
      if (err.response?.status === 401 && err.response?.data?.requiresAuth) {
        // console.log('[AcceptAgentInvitationPage] API requires auth, redirecting to SSO.');
        localStorage.setItem('pendingInvitationToken', token);
        localStorage.removeItem("pendingRedirectUrl");
        
        const ssoCallbackUrl = `${window.location.origin}/auth/sso-callback`;
        const clikkleRedirectDestination = `${clikkleLoginBaseUrl}?redirectto=${encodeURIComponent(ssoCallbackUrl)}`;
        
        setStatus('redirecting_to_sso');
        window.location.href = clikkleRedirectDestination;
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred during direct invitation acceptance.');
        setStatus('error');
      }
    }
  }, [navigate]);

  useEffect(() => {
    // console.log("AcceptAgentInvitationPage: Main effect. Path:", location.pathname, "isAuthenticated:", isAuthenticated, "Current status:", status);
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');

    if (tokenFromUrl && tokenFromUrl !== invitationTokenForUI) {
        setInvitationTokenForUI(tokenFromUrl);
    }

    if (!tokenFromUrl) {
      if (status === 'idle') {
        setError('Invalid invitation link. No token provided.');
        setStatus('error');
      }
      return;
    }

    if (isAuthenticated && currentUser) {
      if (status === 'idle') {
        directlyAcceptInvitation(tokenFromUrl, currentUser);
      }
    } else if (!isAuthenticated) {
      // console.log("AcceptAgentInvitationPage: User NOT authenticated, token found. Preparing to redirect to Clikkle LOGIN.", tokenFromUrl);
      
      localStorage.setItem('pendingInvitationToken', tokenFromUrl);
      localStorage.removeItem("pendingRedirectUrl");

      const ssoCallbackUrl = `${window.location.origin}/auth/sso-callback`;
      const clikkleRedirectDestination = `${clikkleLoginBaseUrl}?redirectto=${encodeURIComponent(ssoCallbackUrl)}`;
            
      // console.log("AcceptAgentInvitationPage: Redirecting to Clikkle Login:", clikkleRedirectDestination);
      setStatus('redirecting_to_sso');
      
      window.location.href = clikkleRedirectDestination;
    }
  }, [location.search, isAuthenticated, currentUser, directlyAcceptInvitation, status, invitationTokenForUI]);

  if (status === 'redirecting_to_sso') {
    return <Container component="main" maxWidth="xs" sx={{ mt: 8, textAlign: 'center' }}><Paper elevation={3} sx={{ p: 4 }}><Typography>Redirecting to sign-in...</Typography><CircularProgress sx={{mt:2}}/></Paper></Container>;
  }

  const publicPaths = ['/accept-agent-invite', '/auth/sso-callback'];
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));
  const userHasNoOrganizations = false; // Replace with actual logic to check if user has no organizations
  if (!isPublicPath && userHasNoOrganizations) {
    navigate('/createOrganization');
  }

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Accept Agent Invitation
        </Typography>

        {(status === 'idle' || status === 'redirecting_to_login') && (
            <Box textAlign="center">
                <Typography>Checking invitation status...</Typography>
                <CircularProgress sx={{mt:2}}/>
            </Box>
        )}

        {status === 'needs_login' && (
          <Box textAlign="center">
            <Typography sx={{ mb: 1 }}>You need to be signed in to accept this invitation.</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>Clicking below will take you to Clikkle to sign in or register. You will be returned here afterwards.</Typography>
            <Button variant="contained" color="primary" onClick={() => {
                 if (invitationTokenForUI) {
                    localStorage.setItem('pendingInvitationToken', invitationTokenForUI);
                    localStorage.removeItem("pendingRedirectUrl");
                    
                    const ssoCallbackUrl = `${window.location.origin}/auth/sso-callback`;
                    const clikkleRedirectDestination = `${clikkleLoginBaseUrl}?redirectto=${encodeURIComponent(ssoCallbackUrl)}`;
                    window.location.href = clikkleRedirectDestination;
                 } else {
                    setError("Invitation token not available for login redirect.");
                    setStatus('error');
                 }
            }} disabled={!invitationTokenForUI}>
              Sign In with Clikkle to Accept
            </Button>
            {!invitationTokenForUI && <Typography color="error" variant="caption" sx={{display: 'block', mt:1}}>Waiting for invitation details...</Typography>}
          </Box>
        )}

        {status === 'processing_acceptance' && (
             <Box textAlign="center">
                <Typography>{message || 'Processing your invitation...'}</Typography>
                <CircularProgress sx={{mt: 2}}/>
            </Box>
        )}

        {status === 'success' && (
          <Box textAlign="center">
            <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>
            <Button component={RouterLink} to="/listOrganization" variant="outlined">
              Go to Organizations
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box textAlign="center">
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button component={RouterLink} to="/" variant="outlined">
              Go to Homepage
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AcceptAgentInvitationPage; 